import {DurationConverter, TimeUtils} from '@colonist/utils'
import {useEffect, useRef, useState} from 'react'

interface GameTimerProps {
    timeLeftInSeconds: number
    isPaused: boolean
}

export const useGameTimer = ({
    timeLeftInSeconds,
    isPaused,
}: GameTimerProps) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const timeEndTimestampRef = useRef(Date.now() + DurationConverter.secondsAsMilliseconds(timeLeftInSeconds))
    const pausedRemainingTimeMsRef = useRef(0)
    const [liveTimeLeftInSeconds, setLiveTimeLeftInSeconds] = useState(timeLeftInSeconds)

    const getSecondsLeft = () => {
        const secondsLeft = Math.max(Math.round((timeEndTimestampRef.current - Date.now()) / TimeUtils.MILLISECONDS), 0)
        return secondsLeft
    }

    const updateLiveTimeLeft = () => {
        const secondsLeft = getSecondsLeft()
        setLiveTimeLeftInSeconds(secondsLeft)
    }

    const removeInterval = () => {
        if(intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }

    const startTimer = () => {
        if(isPaused) return

        pausedRemainingTimeMsRef.current = 0
        updateLiveTimeLeft()
        removeInterval()
        intervalRef.current = setInterval(() => {
            updateLiveTimeLeft()
            if(getSecondsLeft() <= 0) removeInterval()
        }, 1000)
    }

    const pauseTimer = () => {
        pausedRemainingTimeMsRef.current = timeEndTimestampRef.current - Date.now()
        removeInterval()
    }

    const resumeTimer = () => {
        timeEndTimestampRef.current = Date.now() + pausedRemainingTimeMsRef.current
        startTimer()
    }

    useEffect(() => {
        const isAlreadyPaused = pausedRemainingTimeMsRef.current > 0
        if(isPaused && isAlreadyPaused) return
        if(isPaused && !isAlreadyPaused) pauseTimer()
        if(!isPaused && isAlreadyPaused) resumeTimer()
        if(!isPaused && !isAlreadyPaused) startTimer()

        return () => {
            removeInterval()
        }
    }, [isPaused])

    useEffect(() => {
        pausedRemainingTimeMsRef.current = 0
        timeEndTimestampRef.current = Date.now() + DurationConverter.secondsAsMilliseconds(timeLeftInSeconds)
        updateLiveTimeLeft()
        startTimer()
    }, [timeLeftInSeconds])

    return liveTimeLeftInSeconds
}
